import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from '@/components/ui/toast'
import { ConfirmDeleteDialog } from '@/components/shared'
import { useDeleteQuote } from '@/hooks/useQuotes'
import { getApiErrorMessage } from '@/services/api'
import type { Quote } from '@/types'

interface Props {
  quote: Quote | null
  onClose: () => void
  onDeleted?: () => void
  // When true, navigate to the quote list after a successful delete (2.5.11-019).
  navigateOnDelete?: boolean
}

// 2.5.11 Delete quote — Figma 1336:31123.
export function DeleteQuoteDialog({
  quote,
  onClose,
  onDeleted,
  navigateOnDelete,
}: Props) {
  const { t } = useTranslation('billing')
  const navigate = useNavigate()
  const del = useDeleteQuote()

  const confirm = async () => {
    if (!quote) return
    // AC-2.5.11-015: a converted quote cannot be deleted.
    if (quote.converted_invoice_id || quote.converted_contract_id) {
      toast({
        variant: 'destructive',
        title: t('deleteQuote.convertedBlocked'),
      })
      onClose()
      return
    }
    try {
      await del.mutateAsync(quote.id)
      toast({ variant: 'success', title: t('deleteQuote.success') })
      onDeleted?.()
      if (navigateOnDelete) navigate('/quotes')
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('deleteQuote.error')),
      })
    }
  }

  return (
    <ConfirmDeleteDialog
      open={!!quote}
      onOpenChange={o => !o && onClose()}
      title={t('deleteQuote.title')}
      description={t('deleteQuote.confirmText')}
      confirmLabel={t('deleteQuote.confirm')}
      onConfirm={confirm}
      busy={del.isPending}
    />
  )
}
